import type { CategoryId, GameEvent } from './types'
import { EPOCH_UTC, PUZZLE_SIZE } from './constants'
import { POOL } from '../data'

/* ------------------------------------------------------------------ *
 * Deterministic daily puzzle.
 *
 * Everything below is a pure function of (categoryId, dayIndex). Given
 * the same UTC day, every player worldwide gets the exact same six
 * events in the exact same starting order — no backend required.
 * ------------------------------------------------------------------ */

/** Number of whole UTC days between the epoch and `now`. */
export function dayIndex(now: Date = new Date()): number {
  const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  return Math.floor((todayUTC - EPOCH_UTC) / 86_400_000)
}

/** Human-facing puzzle number (Puzzle #1, #2, ...). */
export function puzzleNumber(day: number = dayIndex()): number {
  return day + 1
}

/** ISO "YYYY-MM-DD" key for the current UTC day — used for persistence. */
export function dayKey(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10)
}

/** Milliseconds until the next 00:00 UTC, for the countdown timer. */
export function msUntilNextUTCMidnight(now: Date = new Date()): number {
  const next = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
    0,
    0,
    0,
    0,
  )
  return next - now.getTime()
}

/* --- seeded RNG (mulberry32 + a small string hash) --- */

function hashString(str: string): number {
  let h = 2166136261 >>> 0
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return function () {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Fisher–Yates shuffle in place using the supplied RNG. */
function shuffle<T>(arr: T[], rng: () => number): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

function byDate(a: GameEvent, b: GameEvent): number {
  return a.date < b.date ? -1 : a.date > b.date ? 1 : 0
}

export interface DailyPuzzle {
  category: CategoryId
  day: number
  number: number
  /** The six events in their (deterministic, non-sorted) starting order. */
  events: GameEvent[]
  /** The same events sorted earliest → latest; the solution. */
  solution: GameEvent[]
}

/**
 * Core puzzle builder. Picks PUZZLE_SIZE events from the pool using `seed` and
 * presents them in an order guaranteed not to already be chronological.
 */
function buildPuzzle(
  category: CategoryId,
  seed: number,
): Pick<DailyPuzzle, 'category' | 'events' | 'solution'> {
  const pool = POOL[category]
  const rng = mulberry32(seed)

  const picked = shuffle(pool.slice(), rng).slice(0, PUZZLE_SIZE)
  const solution = picked.slice().sort(byDate)
  const solvedIds = solution.map((e) => e.id).join('|')

  const events = picked.slice()
  let guard = 0
  do {
    shuffle(events, rng)
    guard++
  } while (events.map((e) => e.id).join('|') === solvedIds && guard < 12)

  return { category, events, solution }
}

/**
 * The deterministic puzzle for a category on a given UTC day — same for every
 * player worldwide.
 */
export function getDailyPuzzle(
  category: CategoryId,
  day: number = dayIndex(),
): DailyPuzzle {
  return {
    ...buildPuzzle(category, hashString(`${category}:${day}`)),
    day,
    number: puzzleNumber(day),
  }
}

/**
 * A non-daily puzzle for endless/practice mode, varied by `nonce`. Not seeded
 * by the date, so it doesn't touch the daily streak.
 */
export function getEndlessPuzzle(
  category: CategoryId,
  nonce: number,
): Pick<DailyPuzzle, 'category' | 'events' | 'solution'> {
  return buildPuzzle(category, hashString(`endless:${category}:${nonce}`))
}
